import React, { useState } from 'react';
import styled from 'styled-components';
import { CSSTransition } from 'react-transition-group';
import {
  Box,
  Button,
  ConfirmPopover,
  DropdownMenu,
  Flex,
  Menu,
  MenuItem,
  Popover,
  Select,
  MultiSelect,
  showSuccessToast,
  showWarningToast,
  Spinner,
  Tooltip
} from 'core/components';
import { observer } from 'mobx-react';
import { Classes, Position } from '@blueprintjs/core';
import LabelMapping from 'app/components/labels/LabelMappingNew';
import { InputGroup } from 'core/form';

const FloatingBulkActionsBar = styled.div`
  position: absolute;
  background: #1f1f1f;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 48px;
  border-radius: 8px;
  width: fit-content;
  padding: 0 16px;
  opacity: 0;

  bottom: 90px;
  right: 0;
  left: 0;

  margin-left: auto;
  margin-right: auto;

  &.floating-bulk-actions-enter {
    opacity: 0;
    transform: translateY(50px);
  }

  &.floating-bulk-actions-enter-done {
    opacity: 1;
  }

  &.floating-bulk-actions-enter-active {
    opacity: 1;
    transition: all 250ms;
    transform: translateY(0px);
  }

  &.floating-bulk-actions-exit {
    opacity: 1;
  }

  &.floating-bulk-actions-exit-active {
    opacity: 0;
    transition: all 100ms;
    transform: translateY(20px);
  }

  &.floating-bulk-actions-exit-done {
    opacity: 0;
    pointer-events: none;
  }
`;

const Divider = styled.div`
  width: 1px;
  height: 30px;
  background: rgb(167 182 194 / 22%);
  display: inline-block;
`;

function FreeformText({ collection, action, onChange }) {
  const { selected } = collection;
  let initialValue = '';

  if (Array.isArray(selected) && selected.length > 0) {
    const firstValue = selected[0].get(action.name);
    const sameValue = selected.every((model) => model.get(action.name) === firstValue);
    if (sameValue) {
      initialValue = firstValue;
    }
  }

  const [value, setValue] = useState(initialValue);

  return (
    <Box p={1}>
      <InputGroup
        placeholder={action.label}
        value={value}
        onChange={(evt) => setValue(evt.target.value)}
        rightElement={
          <Button
            minimal
            intent="success"
            icon="tick"
            title="Save"
            className={Classes.POPOVER_DISMISS}
            onClick={() => onChange(value)}
          />
        }
      />
    </Box>
  );
}

export const bulkActionsPopoverModifiers = {
  offset: { enabled: true, offset: '0, 10px' }
};

export const bulkActionsPopoverProps = {
  minimal: false,
  position: Position.TOP,
  modifiers: bulkActionsPopoverModifiers
};

@observer
class FloatingBulkActions extends React.Component {
  static defaultProps = {
    labelType: '',
    selectedLabelSingular: 'item',
    selectedLabelPlural: 'items',
    actions: []
  };

  ref = React.createRef();

  get numSelected() {
    const { collection } = this.props;
    return collection?.selected?.length || 0;
  }

  get itemLabel() {
    const { selectedLabelSingular, selectedLabelPlural } = this.props;
    return this.numSelected > 1 ? selectedLabelPlural : selectedLabelSingular;
  }

  handleClose = () => {
    const { collection } = this.props;
    collection.clearSelection();
  };

  handleChange = (name, value) => {
    const { collection } = this.props;
    const { numSelected, itemLabel } = this;
    return collection
      .bulkUpdate(name, value)
      .then(() => showSuccessToast(`Successfully updated ${numSelected} ${itemLabel}`));
  };

  handleDelete = () => {
    const { collection } = this.props;
    const { itemLabel } = this;
    return collection.bulkDelete().then((result) => {
      if (result.hasErrors) {
        showWarningToast(
          `Deleted ${result.successCount} ${itemLabel}. Failed to delete ${result.errorCount} ${result.errorCount === 1 ? itemLabel : `${itemLabel}s`}.`
        );
      } else {
        showSuccessToast(
          `Successfully removed ${result.successCount} ${result.successCount === 1 ? itemLabel : `${itemLabel}s`}.`
        );
      }
    });
  };

  render() {
    const { collection, actions, labelType, selectedLabelSingular, selectedLabelPlural } = this.props;
    const { numSelected } = this;
    const showBulkActions = numSelected > 0;
    const selectedText = `${numSelected} ${this.itemLabel}`;

    return (
      <>
        <CSSTransition nodeRef={this.ref} in={showBulkActions} timeout={300} classNames="floating-bulk-actions">
          <FloatingBulkActionsBar className="bp4-dark" aria-live="assertive" ref={this.ref}>
            <Flex alignItems="center" gap={1}>
              <Tooltip content="Clear selections" disabled={!showBulkActions}>
                <Button minimal icon="cross" onClick={this.handleClose} text={selectedText} fontWeight="bold" />
              </Tooltip>
              {collection.isRequestActive('updating') && (
                <Spinner size={16} aria-live="polite" title="Saving changes…" />
              )}
              <Divider />
              {actions.map((action) => {
                if (action.component === false || action.component === null) {
                  return null;
                }

                if (action.component) {
                  return (
                    <Box key={action.name} {...action.containerProps}>
                      {action.component}
                    </Box>
                  );
                }

                if (action.name === 'LABELS') {
                  return (
                    <LabelMapping
                      key="labels"
                      collection={collection}
                      type={action.type || labelType}
                      showSelected={false}
                      minimal
                      text="Labels"
                      popoverProps={{ minimal: false, position: Position.TOP, modifiers: bulkActionsPopoverModifiers }}
                      onLabelChange={action.handler}
                    />
                  );
                }

                if (action.name === 'REMOVE') {
                  let confirmBodyContent = `Are you sure you want to remove ${numSelected > 1 ? `these ${numSelected} ${selectedLabelPlural}` : `this ${selectedLabelSingular}`}?`;

                  if (action.confirmBody && numSelected > 0) {
                    if (typeof action.confirmBody === 'function') {
                      confirmBodyContent = action.confirmBody(collection.selected);
                    } else {
                      confirmBodyContent = action.confirmBody;
                    }
                  }

                  return (
                    <ConfirmPopover
                      key="remove"
                      confirmText="Remove"
                      onConfirm={action.handler || this.handleDelete}
                      modifiers={bulkActionsPopoverModifiers}
                      confirmBodyContent={confirmBodyContent}
                    >
                      <Button minimal text="Remove" icon="trash" />
                    </ConfirmPopover>
                  );
                }

                if (action.name === 'MORE') {
                  return (
                    <React.Fragment key="more">
                      <Divider />
                      <DropdownMenu
                        minimal={false}
                        position={Position.TOP}
                        modifiers={bulkActionsPopoverModifiers}
                        content={
                          action.menu || (
                            <Menu>
                              {action.options?.map((option) => (
                                <MenuItem
                                  key={option.value}
                                  text={option.label}
                                  onClick={() => option.handler(action.value)}
                                />
                              ))}
                            </Menu>
                          )
                        }
                      >
                        <Button minimal icon={action.icon || 'more'} text={action.label || 'More'} ariaIsMenuButton />
                      </DropdownMenu>
                    </React.Fragment>
                  );
                }

                if (Array.isArray(action.values)) {
                  return (
                    <MultiSelect
                      minimal
                      key={action.name}
                      options={action.options}
                      valueRenderer={() => action.label}
                      showFilter
                      width="auto"
                      popoverMinimal={false}
                      popoverPosition={Position.TOP}
                      modifiers={bulkActionsPopoverModifiers}
                      onChange={action.handler || ((value) => this.handleChange(action.name, value))}
                      values={action.values}
                      renderAsButton
                      toggle
                      keepOpen
                    />
                  );
                }

                if (action.options) {
                  return (
                    <Select
                      minimal
                      key={action.name}
                      options={action.options}
                      valueRenderer={() => action.label}
                      showFilter
                      width="auto"
                      popoverMinimal={false}
                      popoverPosition={Position.TOP}
                      modifiers={bulkActionsPopoverModifiers}
                      onChange={action.handler || ((value) => this.handleChange(action.name, value))}
                    />
                  );
                }

                if (action.freeform) {
                  return (
                    <Popover
                      key={action.name}
                      minimal={false}
                      position={Position.TOP}
                      modifiers={bulkActionsPopoverModifiers}
                      content={
                        <FreeformText
                          collection={collection}
                          action={action}
                          onChange={action.handler || ((value) => this.handleChange(action.name, value))}
                        />
                      }
                    >
                      <Button minimal icon={action.icon} rightIcon="caret-down" text={action.label} />
                    </Popover>
                  );
                }

                return (
                  <Button
                    minimal
                    key={action.name}
                    text={action.label}
                    onClick={action.handler}
                    intent={action.intent}
                    disabled={action.disabled}
                    icon={action.icon}
                  />
                );
              })}
            </Flex>
          </FloatingBulkActionsBar>
        </CSSTransition>
      </>
    );
  }
}

export default FloatingBulkActions;
